import { useCallback } from 'react';
import useDriftClient from '../../useDriftClient';
import useCurrentUserAccount from '../../useCurrentUserAccount';
import {
	createOpenPerpMarketOrderIxs,
	createSettlePnlIx,
	MarketId,
	OptionalAuctionParamsRequestInputs,
	OptionalTriggerOrderParams,
	PlaceAndTakeParams,
	mapAuctionParamsResponse,
} from '@velocity-exchange/common';
import { PerpTradeFormOutputProps } from 'src/stores/DriftStore/actions/actionHelpers/types';
import Env, {
	syncGetCurrentSettings,
	UserSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import invariant from 'tiny-invariant';
import { ORDER_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/orderPrep';
import {
	PublicKey,
	Transaction,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import useAppEventEmitter from 'src/hooks/useAppEventEmitter';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { TRADE_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/tradePrep';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import {
	generateUserOrderIdForUser,
	getNextUserOrderIdForUser,
} from 'src/utils/order';
import { useTxErrorHandler } from 'src/hooks/driftActions/transactions/useTxErrorHandler';
import {
	BN,
	VelocityClient,
	User,
	PositionDirection,
	TxParams,
	TransactionParamProcessor,
	MAX_LEVERAGE_ORDER_SIZE,
	OptionalOrderParams,
} from '@velocity-exchange/sdk';
import { checkMaxLeverageFlagUsage } from 'src/utils/trade/checkMaxLeverageFlagUsage';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { logOrderParams } from 'src/utils/logging/auctionParams';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { OrderFillTimeTracker } from 'src/utils/orderFillTimeTracker';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { FeePayerError } from 'src/constants/errors/FeePayerError';

type TxnKey = 'perpMarketOrder' | 'settlePnl' | 'fill';

const FALLBACK_ON_FAILED_SIM = true; // This controls whether the drift client should just exit early if the simulated place and take fails. Note: This is intertwined with the logic in the getMakerInfoForMarketOrder method - which currently DOESN'T check for staleness in case of the dlob-server having stale data. The idea for not doing the staleness check is that if it does give a stale response then the sim will fail so we'll skip it because of this parameter. If we want to adjust our strategy we may want to add staleness checking logic in there.

/**
 * Simulates the transaction if it is a placeAndTake perp market order,
 * and will fallback to regular market orders if the simulation fails (depending on `FALLBACK_ON_FAILED_SIM`)
 */
export const simulatableCreatePerpMarketOrderIxs = async (params: {
	marketIndex: number;
	direction: PositionDirection;
	baseAmount: BN;
	optionalAuctionParamsInputs: OptionalAuctionParamsRequestInputs;
	bracketOrders?: {
		takeProfit?: OptionalTriggerOrderParams;
		stopLoss?: OptionalTriggerOrderParams;
	};
	userOrderId: number;
	driftClient: VelocityClient;
	user: User;
	txParams: TxParams;
	placeAndTake: PlaceAndTakeParams;
	mainSignerOverride: PublicKey;
	positionMaxLeverage: number;
	reduceOnly?: boolean;
	orderParamsRef?: { current: OptionalOrderParams | undefined };
	isolatedPositionDepositsOverride?: {
		mainDeposit: BN;
		additionalDeposits?: { marketIndex: number; amount: BN }[];
	};
}): Promise<TransactionInstruction[]> => {
	const {
		marketIndex,
		direction,
		baseAmount,
		optionalAuctionParamsInputs,
		bracketOrders,
		userOrderId,
		placeAndTake,
		driftClient,
		user,
		txParams,
		mainSignerOverride,
		positionMaxLeverage,
		reduceOnly,
		isolatedPositionDepositsOverride,
	} = params;

	const ixs = await createOpenPerpMarketOrderIxs({
		velocityClient: driftClient,
		user,
		assetType: 'base',
		marketIndex,
		direction,
		amount: baseAmount,
		reduceOnly,
		dlobServerHttpUrl: Env.dlobServerHttpUrl,
		optionalAuctionParamsInputs,
		bracketOrders,
		userOrderId,
		placeAndTake,
		mainSignerOverride,
		positionMaxLeverage,
		isolatedPositionDepositsOverride,
		callbacks: {
			onAuctionParamsFetched: (urlSearchParams, response) => {
				logOrderParams(
					MarketId.createPerpMarket(marketIndex),
					user.userAccountPublicKey,
					urlSearchParams,
					response
				);
				if (params.orderParamsRef) {
					params.orderParamsRef.current = mapAuctionParamsResponse(
						response.data.params
					) as OptionalOrderParams;
				}
			},
		},
	});

	// simulate placeAndTake (if enabled), and fallback to regular market order if simulation fails
	// placeAndTake will already fallback to regular market order if no top makers are found above
	if (
		placeAndTake.enable &&
		(!!txParams?.useSimulatedComputeUnits || FALLBACK_ON_FAILED_SIM)
	) {
		const placeAndTakeTxToSim = (await driftClient.buildTransaction(
			ixs,
			txParams
		)) as VersionedTransaction;
		const simulationResult =
			await TransactionParamProcessor.getTxSimComputeUnits(
				placeAndTakeTxToSim,
				driftClient.connection,
				txParams.computeUnitsBufferMultiplier ?? 1.2,
				txParams.lowerBoundCu
			);

		if (!simulationResult.success) {
			console.error('placeAndTake simulation failed', simulationResult);

			if (FALLBACK_ON_FAILED_SIM) {
				return await createOpenPerpMarketOrderIxs({
					velocityClient: driftClient,
					user,
					assetType: 'base',
					marketIndex,
					direction,
					amount: baseAmount,
					reduceOnly,
					dlobServerHttpUrl: Env.dlobServerHttpUrl,
					optionalAuctionParamsInputs,
					bracketOrders,
					userOrderId,
					positionMaxLeverage,
					isolatedPositionDepositsOverride,
					mainSignerOverride,
					callbacks: {
						onAuctionParamsFetched: (urlSearchParams, response) => {
							logOrderParams(
								MarketId.createPerpMarket(marketIndex),
								user.userAccountPublicKey,
								urlSearchParams,
								response
							);
							if (params.orderParamsRef) {
								params.orderParamsRef.current = mapAuctionParamsResponse(
									response.data.params
								) as OptionalOrderParams;
							}
						},
					},
					// use regular market orders instead of placeAndTake
				});
			} else {
				console.warn(
					'continue with placing the placeAndTake order even though simulation failed'
				);
			}
		}

		// understandably not the best code to mutate the txParams for now
		txParams.computeUnits = simulationResult.computeUnits;
	}

	return ixs;
};

export const useOpenPerpMarketOrder = () => {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const appEventEmitter = useAppEventEmitter();
	const setDriftStore = useDriftStore((s) => s.set);
	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();
	const getOraclePrice = useGetOraclePriceForMarket();

	const openPerpMarketOrder = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				_toastId: string, // toasts are handled by MarketOrderToastEventEmitterProvider
				perpTradeFormOutputProps: PerpTradeFormOutputProps & {
					leadSide: 'base' | 'quote';
				}
			): Promise<string | undefined> => {
				invariant(driftClient, 'driftClient is required');
				invariant(currentUserAccount?.client, 'currentUserAccount is required');

				const {
					direction,
					baseAmountBigNum,
					bracketOrders,
					optionalAuctionParamsInputs,
					numOfOrders,
				} = ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps(
					perpTradeFormOutputProps
				);

				const settings = syncGetCurrentSettings();
				const marketId = MarketId.createPerpMarket(
					perpTradeFormOutputProps.targetMarketIndex
				);
				const isPlaceAndTake =
					driftClient.txVersion === 0 && !!settings?.placeAndTakeEnabled;
				const userOrderId = generateUserOrderIdForUser(
					currentUserAccount.client
				);

				const auctionToastEventProps =
					TRADE_PREP_UTILS.getAuctionToastEventProps({
						marketId,
						direction,
						baseSize: baseAmountBigNum,
						numOrders: numOfOrders,
						currentSettings: settings as Partial<UserSettings>,
						bracketOrders: perpTradeFormOutputProps.bracketOrders,
						orderType: perpTradeFormOutputProps.orderType,
						userOrderId: userOrderId,
					});

				appEventEmitter.emit('expectedAuctionInit', {
					...auctionToastEventProps,
					isAutoSigned,
				});

				try {
					let createSettlePnlIxPromise: Promise<
						TransactionInstruction | undefined
					> = Promise.resolve(undefined);
					let createFillIxPromise: Promise<TransactionInstruction | undefined> =
						Promise.resolve(undefined);

					const txParams = getTxParams({
						computeUnits: isPlaceAndTake ? MAX_COMPUTE_UNITS : undefined,
						marketId,
						simulatedComputeUnitMultiplier: isPlaceAndTake ? 1.4 : undefined,
						lowerBoundCu: isPlaceAndTake
							? Env.placeAndTakeLowerBoundCu
							: undefined,
					});

					// safety check for max leverage flag usage
					const useMaxLeverage = checkMaxLeverageFlagUsage(
						!!perpTradeFormOutputProps.maxLeverageSelected,
						perpTradeFormOutputProps.targetMarketIndex,
						baseAmountBigNum,
						getOraclePrice(marketId),
						currentUserAccount.client,
						direction,
						perpTradeFormOutputProps.displayLeverage
					);
					const baseAmountToUseBN = useMaxLeverage
						? MAX_LEVERAGE_ORDER_SIZE
						: baseAmountBigNum.val;

					const orderParamsRef: { current: OptionalOrderParams | undefined } = {
						current: undefined,
					};
					console.log(
						'perpTradeFormOutputProps.additionalIsolatedPositionDeposits',
						perpTradeFormOutputProps.additionalIsolatedPositionDeposits?.map(
							(m) => m.marketIndex
						)
					);
					const createPerpMarketOrderIxPromise =
						simulatableCreatePerpMarketOrderIxs({
							driftClient,
							user: currentUserAccount.client,
							marketIndex: perpTradeFormOutputProps.targetMarketIndex,
							direction,
							baseAmount: baseAmountToUseBN,
							reduceOnly: perpTradeFormOutputProps.reduceOnly,
							optionalAuctionParamsInputs,
							bracketOrders,
							userOrderId,
							placeAndTake: {
								enable: isPlaceAndTake,
								auctionDurationPercentage: Env.defaultAuctionDurationPercentage,
								referrerInfo: driftClient
									.getUserStatsOrThrow()
									.getReferrerInfo(),
							},
							txParams,
							mainSignerOverride: signerWallet.publicKey,
							positionMaxLeverage: perpTradeFormOutputProps.displayLeverage,
							orderParamsRef,
							isolatedPositionDepositsOverride:
								perpTradeFormOutputProps.isolatedPositionDeposit
									? {
											mainDeposit:
												perpTradeFormOutputProps.isolatedPositionDeposit,
											additionalDeposits:
												perpTradeFormOutputProps.additionalIsolatedPositionDeposits,
										}
									: undefined,
						});

					// create a settle pnl ix if the market order is reduce only. this eventual settle pnl txn will be sent
					// after the UI detects that the market order has been filled
					if (perpTradeFormOutputProps.reduceOnly) {
						createSettlePnlIxPromise = createSettlePnlIx({
							velocityClient: driftClient,
							user: currentUserAccount.client,
							marketIndexes: [perpTradeFormOutputProps.targetMarketIndex],
						});
					}

					// create a fill ix, where the eventual fill txn will be sent after the UI detects that the market order
					// has not been filled during the auction stage
					if (driftClient.txVersion === 0) {
						const userAccount =
							currentUserAccount.client.getUserAccountOrThrow();
						const nextOrderId = userAccount.nextOrderId;
						createFillIxPromise = driftClient.getFillPerpOrderIx(
							currentUserAccount.client.getUserAccountPublicKey(),
							userAccount,
							{
								marketIndex: perpTradeFormOutputProps.targetMarketIndex,
								orderId: nextOrderId,
							},
							undefined,
							userAccount.subAccountId
						);
					}

					const [perpMarketOrderIx, settlePnlIx, fillIx] = await Promise.all([
						createPerpMarketOrderIxPromise,
						createSettlePnlIxPromise,
						createFillIxPromise,
					]);

					if (!perpMarketOrderIx) {
						return undefined;
					}

					const ixsToSign: Partial<
						Record<TxnKey, TransactionInstruction | TransactionInstruction[]>
					> = {
						perpMarketOrder: perpMarketOrderIx,
						settlePnl: settlePnlIx,
						fill: fillIx,
					};

					const { signedTxData, signedTxMap } =
						await signerWallet.buildAndSignTransactionsMap(ixsToSign, txParams);

					const auctionVersion = useFeatureFlagStore
						.getState()
						.flagEnabled('AUCTION_PARAMS_VERSION')
						? 2
						: 1;

					// For tracking, use the actual nextOrderId from the account (not the generated one)
					const actualOrderId = getNextUserOrderIdForUser(
						currentUserAccount.client
					);

					// If orderParamsRef.current is undefined, construct basic order params
					const orderParamsForTracking: OptionalOrderParams =
						orderParamsRef.current || {
							orderType: { market: {} },
							marketIndex: perpTradeFormOutputProps.targetMarketIndex,
							direction,
							baseAssetAmount: baseAmountToUseBN,
							marketType: { perp: {} },
							userOrderId,
							reduceOnly: perpTradeFormOutputProps.reduceOnly,
						};

					OrderFillTimeTracker.trackOrderSubmission(
						actualOrderId,
						Date.now(),
						orderParamsForTracking,
						'legacy',
						currentUserAccount.pubKey.toString(),
						auctionVersion,
						undefined,
						getOraclePrice(marketId).val
					);

					appEventEmitter.emit('expectedAuctionTxSigned', {
						...auctionToastEventProps,
						signedTxData,
					});

					invariant(
						signedTxMap.perpMarketOrder,
						'signed perpMarketOrder is required'
					);
					const { txSig } = await driftClient.sendTransaction(
						signedTxMap.perpMarketOrder,
						undefined,
						undefined,
						true // signed above with driftClient.txHandler.getSignedTransactionMap
					);

					appEventEmitter.emit('tradeConfirmation');
					appEventEmitter.emit(
						'expectedAuctionTxConfirmed',
						auctionToastEventProps
					);

					if (signedTxMap.settlePnl) {
						setDriftStore((s) => {
							s.pnlsAwaitingAutoSettle.push({
								marketIndexesOfPnls: [
									perpTradeFormOutputProps.targetMarketIndex,
								],
								signedSettlePnlsTx: signedTxMap.settlePnl as
									| Transaction
									| VersionedTransaction,
							});
						});
					}

					if (signedTxMap.fill) {
						appEventEmitter.emit('fallbackAuctionOrderSignedTx', {
							tx: signedTxMap.fill as Transaction,
							orderId: userOrderId,
						});
					}

					return txSig;
				} catch (e) {
					console.error('Error opening perp market order', e);

					if (e instanceof FeePayerError) {
						// handled by useDriftActionWithFallback
						throw e;
					}

					// Check if this is a transaction size error with additional isolated deposits
					const hasAdditionalDeposits =
						!!perpTradeFormOutputProps.additionalIsolatedPositionDeposits
							?.length;
					const isTxSizeError = (e as Error)?.message?.match(
						/\(max: encoded\/raw \d+\/\d+\)/
					);

					if (hasAdditionalDeposits && isTxSizeError) {
						appEventEmitter.emit(
							'expectedAuctionTxFailed',
							auctionToastEventProps
						);
						handleTxError(
							new Error(
								'Transaction too large due to isolated position top-ups. Please manually add margin to your under-collateralized isolated positions or reduce their size before placing this order.'
							),
							auctionToastEventProps.v2Props.userOrderId?.toString() ?? 'order',
							'Transaction Too Large'
						);
						return undefined;
					}

					appEventEmitter.emit(
						'expectedAuctionTxFailed',
						auctionToastEventProps
					);

					return undefined;
				}
			},
			[
				driftClient,
				currentUserAccount?.client,
				appEventEmitter,
				setDriftStore,
				getOraclePrice,
				getTxParams,
				handleTxError,
			]
		)
	);

	return openPerpMarketOrder;
};
